import React, { useState, useRef, useEffect } from 'react';
import './BarcodeScanner.css';

const BarcodeScanner = ({ onBarcodeDetected, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [focusStatus, setFocusStatus] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      
      // Load ZXing library
      await loadZXingLibrary();

      // Try to get the main rear camera (not wide-angle or macro)
      let stream;
      
      // Get all available cameras and let user cycle through them
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      console.log('📱 Available cameras:', videoDevices.length);
      setAvailableCameras(videoDevices);
      
      // Use saved camera preference or find best default
      let selectedCamera;
      let finalCameraIndex = currentCameraIndex;
      
      if (videoDevices.length > 0) {
        // First, try to match saved camera by device ID (most reliable)
        const savedCameraId = getCookie('preferred_camera_id');
        const savedCameraLabel = getCookie('preferred_camera_label');
        
        if (savedCameraId) {
          const savedCamera = videoDevices.find(device => device.deviceId === savedCameraId);
          if (savedCamera) {
            finalCameraIndex = videoDevices.indexOf(savedCamera);
            selectedCamera = savedCamera;
            console.log('✅ Using saved camera by ID:', savedCamera.label);
            setFocusStatus(`Using saved camera: ${savedCamera.label?.substring(0, 25) || 'Unknown'}...`);
          } else if (savedCameraLabel) {
            // Fallback: try to match by label if device ID changed
            const labelMatch = videoDevices.find(device => 
              device.label === savedCameraLabel
            );
            if (labelMatch) {
              finalCameraIndex = videoDevices.indexOf(labelMatch);
              selectedCamera = labelMatch;
              console.log('✅ Using saved camera by label:', labelMatch.label);
              setFocusStatus(`Using saved camera: ${labelMatch.label?.substring(0, 25)}...`);
            }
          }
        }
        
        // If no saved camera found, use current index or find best default
        if (!selectedCamera) {
          if (currentCameraIndex < videoDevices.length) {
            selectedCamera = videoDevices[currentCameraIndex];
            finalCameraIndex = currentCameraIndex;
          } else {
            // Reset to first camera if index is out of bounds
            finalCameraIndex = 0;
            selectedCamera = videoDevices[0];
          }
          
          // If this is the first load and no saved preference, try to find the best camera automatically
          if (currentCameraIndex === 0 && videoDevices.length > 1 && !savedCameraId) {
            const mainCamera = videoDevices.find(device => 
              device.label.toLowerCase().includes('back') && 
              !device.label.toLowerCase().includes('wide') &&
              !device.label.toLowerCase().includes('ultra') &&
              !device.label.toLowerCase().includes('macro')
            ) || videoDevices.find(device => 
              device.label.toLowerCase().includes('rear')
            );
            
            if (mainCamera) {
              finalCameraIndex = videoDevices.indexOf(mainCamera);
              selectedCamera = mainCamera;
            }
          }
        }
        
        // Update state if camera index changed
        if (finalCameraIndex !== currentCameraIndex) {
          setCurrentCameraIndex(finalCameraIndex);
        }
        
        console.log(`📷 Using camera ${finalCameraIndex + 1}/${videoDevices.length}:`, selectedCamera.label);
        
        const cameraName = selectedCamera.label || `Camera ${finalCameraIndex + 1}`;
        if (!getCookie('preferred_camera_id')) {
          setFocusStatus(`Camera ${finalCameraIndex + 1}/${videoDevices.length}: ${cameraName.substring(0, 25)}...`);
        }
        
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedCamera.deviceId },
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        });
      } else {
        // Fallback if no specific cameras found
        setFocusStatus('Using default camera');
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { exact: 'environment' },
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        });
      }
      
      // Configure camera for optimal barcode scanning
      const track = stream.getVideoTracks()[0];
      if (track && track.getCapabilities) {
        const capabilities = track.getCapabilities();
        
        // Apply optimal settings for barcode scanning
        const constraints = {};
        
        // Enable continuous autofocus if available
        if (capabilities.focusMode && capabilities.focusMode.includes('continuous')) {
          constraints.focusMode = 'continuous';
        }
        
        // Set focus distance for close-up scanning if available
        if (capabilities.focusDistance) {
          constraints.focusDistance = 0.1; // Close focus
        }
        
        // Enable continuous exposure if available
        if (capabilities.exposureMode && capabilities.exposureMode.includes('continuous')) {
          constraints.exposureMode = 'continuous';
        }
        
        // Apply constraints if any are available
        if (Object.keys(constraints).length > 0) {
          try {
            // Try advanced constraints first
            await track.applyConstraints({ advanced: [constraints] });
            console.log('Applied advanced camera optimizations');
          } catch (err) {
            // Try basic constraints if advanced fail
            try {
              await track.applyConstraints(constraints);
              console.log('Applied basic camera optimizations');
            } catch (err2) {
              console.log('Camera optimizations not supported on this device');
            }
          }
        }
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setHasPermission(true);
        setIsScanning(true);
        
        // Start scanning after video loads and camera stabilizes
        videoRef.current.onloadedmetadata = () => {
          // Wait longer for camera to focus and stabilize
          setTimeout(() => {
            startBarcodeDetection();
          }, 2500);
        };
        
        // Show camera info on screen
        const track = stream.getVideoTracks()[0];
        if (track) {
          const settings = track.getSettings();
          const capabilities = track.getCapabilities();
          
          console.log('📷 Camera settings:', settings);
          console.log('📷 Camera capabilities:', Object.keys(capabilities));
          
          // Show camera info to user
          const cameraInfo = [];
          if (settings.width && settings.height) {
            cameraInfo.push(`${settings.width}x${settings.height}`);
          }
          if (capabilities.focusMode) {
            cameraInfo.push('Focus: ' + capabilities.focusMode.join(', '));
          }
          if (capabilities.focusDistance) {
            cameraInfo.push('Distance control');
          }
          
          if (cameraInfo.length > 0) {
            const cameraName = availableCameras[currentCameraIndex]?.label || `Camera ${currentCameraIndex + 1}`;
            const isPreferred = getCookie('preferred_camera_id') === availableCameras[currentCameraIndex]?.deviceId;
            const preferredText = isPreferred ? ' ★ SAVED' : '';
            
            setFocusStatus(`${currentCameraIndex + 1}/${availableCameras.length}: ${cameraName.substring(0, 15)}${preferredText} • ${cameraInfo.join(' • ')}`);
            
            // Clear after showing for a few seconds
            setTimeout(() => {
              if (availableCameras.length > 1) {
                const savedIndicator = isPreferred ? ' ★' : '';
                setFocusStatus(`Camera ${currentCameraIndex + 1}/${availableCameras.length}${savedIndicator} • Tap 🔄 to switch`);
              } else {
                setFocusStatus('');
              }
            }, 4000);
          }
        }
        
        // Make video definitely tappable
        videoRef.current.style.cursor = 'pointer';
        videoRef.current.style.userSelect = 'none';
        videoRef.current.style.pointerEvents = 'auto';
        videoRef.current.style.position = 'relative';
        videoRef.current.style.zIndex = '1';
        
        const handleTap = (e) => {
          console.log('🎯 TAP DETECTED ON VIDEO!');
          console.log('Event type:', e.type);
          console.log('Target:', e.target.tagName);
          e.preventDefault();
          e.stopPropagation();
          
          // Immediate visual feedback
          videoRef.current.style.border = '3px solid red';
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.style.border = 'none';
            }
          }, 500);
          
          handleTapToFocus(e, track);
        };
        
        // Test if events work at all
        const testHandler = (e) => {
          console.log('🟢 TEST EVENT FIRED:', e.type);
        };
        
        // Attach multiple event types with debugging
        videoRef.current.addEventListener('click', handleTap, { passive: false });
        videoRef.current.addEventListener('touchstart', testHandler, { passive: false });
        videoRef.current.addEventListener('touchend', handleTap, { passive: false });
        videoRef.current.addEventListener('mousedown', testHandler, { passive: false });
        
        console.log('✅ Event handlers attached to video element');
        
        // Store the track reference for the manual focus button
        videoRef.current.cameraTrack = track;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Camera access denied or not available. Please ensure you have granted camera permissions.');
      setHasPermission(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsScanning(false);
  };

  const loadZXingLibrary = () => {
    return new Promise((resolve, reject) => {
      if (window.ZXing && window.ZXing.BrowserMultiFormatReader) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@zxing/library@0.20.0/umd/index.min.js';
      
      script.onload = () => {
        // Wait a moment for the library to initialize
        setTimeout(() => {
          if (window.ZXing && window.ZXing.BrowserMultiFormatReader) {
            resolve();
          } else {
            reject(new Error('ZXing library incomplete'));
          }
        }, 500);
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load ZXing library'));
      };
      
      document.head.appendChild(script);
    });
  };

  const startBarcodeDetection = async () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas not available');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Force scanning to true at start
    setIsScanning(true);

    let scanCount = 0;
    let localIsScanning = true; // Local variable to avoid state issues
    
    const detectBarcodes = async () => {
      if (!localIsScanning) {
        return;
      }
      
      if (!video.videoWidth || video.readyState !== video.HAVE_ENOUGH_DATA) {
        return;
      }

      scanCount++;
      
      try {
        // Set canvas size to match video dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw current video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);


        // Try multiple ZXing detection approaches
        if (window.ZXing && window.ZXing.BrowserMultiFormatReader) {
          try {
            // Method 1: Standard MultiFormatReader with correct API
            const codeReader = new window.ZXing.BrowserMultiFormatReader();
            let result;
            
            // Method 1A: Try scan line approach first (more efficient)
            result = await tryScanLineDetection(canvas, codeReader, ctx);
            
            // Method 1B: Fallback to full image if scan line fails
            if (!result) {
              if (codeReader.decodeFromImageElement) {
                const imageUrl = canvas.toDataURL();
                const img = new Image();
                img.src = imageUrl;
                await new Promise(resolve => img.onload = resolve);
                result = await codeReader.decodeFromImageElement(img);
              } else if (codeReader.decodeFromImageUrl) {
                const imageUrl = canvas.toDataURL();
                result = await codeReader.decodeFromImageUrl(imageUrl);
              }
            }
            
            
            if (result && result.text) {
              localIsScanning = false;
              setIsScanning(false);
              onBarcodeDetected(result.text, result.format || 'unknown');
              return;
            }
            
            // Method 2: Try specific EAN readers if MultiFormat fails
            if (!result && window.ZXing.BrowserEAN13Reader) {
              try {
                const ean13Reader = new window.ZXing.BrowserEAN13Reader();
                if (ean13Reader.decodeFromImageElement) {
                  const imageUrl = canvas.toDataURL();
                  const img = new Image();
                  img.src = imageUrl;
                  await new Promise(resolve => img.onload = resolve);
                  result = await ean13Reader.decodeFromImageElement(img);
                } else if (ean13Reader.decodeFromImageUrl) {
                  const imageUrl = canvas.toDataURL();
                  result = await ean13Reader.decodeFromImageUrl(imageUrl);
                }
                
                if (result && result.text) {
                  localIsScanning = false;
                  setIsScanning(false);
                  onBarcodeDetected(result.text, 'EAN_13');
                  return;
                }
              } catch (ean13Err) {
                // EAN13 reader failed, continue with other methods
              }
            }
            
            // Method 3: Try scan line processing with enhancement every 30 scans
            if (scanCount % 30 === 0) {
              const enhancedResults = await tryEnhancedScanLines(canvas, codeReader, ctx);
              if (enhancedResults) {
                localIsScanning = false;
                setIsScanning(false);
                onBarcodeDetected(enhancedResults.text, enhancedResults.format || 'unknown');
                return;
              }
            }
            
            // Method 4: Full image processing as last resort every 50 scans
            if (scanCount % 50 === 0) {
              const processedResults = await tryMultipleImageProcessing(canvas, codeReader, ctx);
              if (processedResults) {
                localIsScanning = false;
                setIsScanning(false);
                onBarcodeDetected(processedResults.text, processedResults.format || 'unknown');
                return;
              }
            }
            
          } catch (err) {
            // Continue scanning - errors are normal when no barcode is found
          }
        }
      } catch (err) {
        // Continue scanning despite errors
      }
    };

    // Stop any existing interval first
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    // Initial scan attempt
    setTimeout(detectBarcodes, 100);
    
    // Scan every 500ms for good balance between performance and detection
    scanIntervalRef.current = setInterval(detectBarcodes, 500);
    
    // Add cleanup function to stop scanning when component unmounts
    return () => {
      localIsScanning = false;
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  };

  const handleRetry = () => {
    setError(null);
    setHasPermission(null);
    startCamera();
  };

  // Cookie utility functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  };

  const switchToNextCamera = () => {
    if (availableCameras.length <= 1) {
      setFocusStatus('Only one camera available');
      setTimeout(() => setFocusStatus(''), 2000);
      return;
    }
    
    // Stop current scanning
    setIsScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    // Stop current stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Move to next camera
    const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
    setCurrentCameraIndex(nextIndex);
    
    // Save the new camera preference to cookies
    setCookie('preferred_camera_index', nextIndex.toString());
    
    // Also save camera device ID for more reliable matching
    if (availableCameras[nextIndex]) {
      setCookie('preferred_camera_id', availableCameras[nextIndex].deviceId);
      setCookie('preferred_camera_label', availableCameras[nextIndex].label || '');
    }
    
    setFocusStatus(`Switching to camera ${nextIndex + 1}/${availableCameras.length}... (Saved preference)`);
    
    // Restart with new camera
    setTimeout(() => {
      startCamera();
    }, 500);
  };

  const tryScanLineDetection = async (canvas, codeReader, ctx) => {
    // Extract horizontal scan lines like a real barcode scanner
    const scanLines = [
      canvas.height * 0.4,  // 40% down
      canvas.height * 0.5,  // Center (50%)
      canvas.height * 0.6,  // 60% down
    ];
    
    const scanLineHeight = 8; // Thick scan line for better capture
    
    for (const yPosition of scanLines) {
      try {
        // Create a narrow horizontal strip
        const stripCanvas = document.createElement('canvas');
        stripCanvas.width = canvas.width;
        stripCanvas.height = scanLineHeight;
        const stripCtx = stripCanvas.getContext('2d');
        
        // Extract the scan line from the main canvas
        stripCtx.drawImage(
          canvas,
          0, yPosition - scanLineHeight/2, // Source x, y
          canvas.width, scanLineHeight,    // Source width, height
          0, 0,                           // Dest x, y
          canvas.width, scanLineHeight    // Dest width, height
        );
        
        // Try to decode this scan line
        let result;
        if (codeReader.decodeFromImageElement) {
          const imageUrl = stripCanvas.toDataURL();
          const img = new Image();
          img.src = imageUrl;
          await new Promise(resolve => img.onload = resolve);
          result = await codeReader.decodeFromImageElement(img);
        } else if (codeReader.decodeFromImageUrl) {
          const imageUrl = stripCanvas.toDataURL();
          result = await codeReader.decodeFromImageUrl(imageUrl);
        }
        
        if (result && result.text) {
          return result;
        }
      } catch (err) {
        // Continue to next scan line
      }
    }
    return null;
  };

  const tryMultipleImageProcessing = async (canvas, codeReader, ctx) => {
    const techniques = [
      // 1. High contrast
      (imageData) => {
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          const contrast = avg > 128 ? 255 : 0; // High contrast black/white
          data[i] = data[i + 1] = data[i + 2] = contrast;
        }
        return imageData;
      },
      // 2. Brightness and contrast boost
      (imageData) => {
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, (data[i] - 128) * 2 + 128 + 30);
          data[i + 1] = Math.min(255, (data[i + 1] - 128) * 2 + 128 + 30);
          data[i + 2] = Math.min(255, (data[i + 2] - 128) * 2 + 128 + 30);
        }
        return imageData;
      },
      // 3. Grayscale with sharpening
      (imageData) => {
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
          const enhanced = gray < 100 ? Math.max(0, gray - 20) : Math.min(255, gray + 40);
          data[i] = data[i + 1] = data[i + 2] = enhanced;
        }
        return imageData;
      },
      // 4. Invert colors (for dark barcodes on light backgrounds)
      (imageData) => {
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          data[i] = 255 - data[i];
          data[i + 1] = 255 - data[i + 1];
          data[i + 2] = 255 - data[i + 2];
        }
        return imageData;
      }
    ];

    for (const technique of techniques) {
      try {
        // Create processed canvas
        const processedCanvas = document.createElement('canvas');
        processedCanvas.width = canvas.width;
        processedCanvas.height = canvas.height;
        const processedCtx = processedCanvas.getContext('2d');
        
        // Copy and process image
        processedCtx.drawImage(canvas, 0, 0);
        const imageData = processedCtx.getImageData(0, 0, processedCanvas.width, processedCanvas.height);
        const processedImageData = technique(imageData);
        processedCtx.putImageData(processedImageData, 0, 0);
        
        // Try detection
        let result;
        if (codeReader.decodeFromImageElement) {
          const imageUrl = processedCanvas.toDataURL();
          const img = new Image();
          img.src = imageUrl;
          await new Promise(resolve => img.onload = resolve);
          result = await codeReader.decodeFromImageElement(img);
        } else if (codeReader.decodeFromImageUrl) {
          const imageUrl = processedCanvas.toDataURL();
          result = await codeReader.decodeFromImageUrl(imageUrl);
        }
        
        if (result && result.text) {
          return result;
        }
      } catch (err) {
        // Continue to next technique
      }
    }
    return null;
  };

  const tryEnhancedScanLines = async (canvas, codeReader, ctx) => {
    // Try scan lines with image enhancement
    const scanLines = [
      canvas.height * 0.35,
      canvas.height * 0.4,
      canvas.height * 0.5,  // Center
      canvas.height * 0.6,
      canvas.height * 0.65,
    ];
    
    const scanLineHeight = 12; // Slightly thicker for enhancement
    
    for (const yPosition of scanLines) {
      try {
        // Create scan line strip
        const stripCanvas = document.createElement('canvas');
        stripCanvas.width = canvas.width;
        stripCanvas.height = scanLineHeight;
        const stripCtx = stripCanvas.getContext('2d');
        
        // Extract scan line
        stripCtx.drawImage(
          canvas,
          0, yPosition - scanLineHeight/2,
          canvas.width, scanLineHeight,
          0, 0,
          canvas.width, scanLineHeight
        );
        
        // Apply high contrast enhancement to scan line
        const imageData = stripCtx.getImageData(0, 0, stripCanvas.width, stripCanvas.height);
        const data = imageData.data;
        
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          const enhanced = avg > 120 ? 255 : 0; // High contrast
          data[i] = data[i + 1] = data[i + 2] = enhanced;
        }
        
        stripCtx.putImageData(imageData, 0, 0);
        
        // Try to decode enhanced scan line
        let result;
        if (codeReader.decodeFromImageElement) {
          const imageUrl = stripCanvas.toDataURL();
          const img = new Image();
          img.src = imageUrl;
          await new Promise(resolve => img.onload = resolve);
          result = await codeReader.decodeFromImageElement(img);
        } else if (codeReader.decodeFromImageUrl) {
          const imageUrl = stripCanvas.toDataURL();
          result = await codeReader.decodeFromImageUrl(imageUrl);
        }
        
        if (result && result.text) {
          return result;
        }
      } catch (err) {
        // Continue to next scan line
      }
    }
    return null;
  };

  const handleManualEntry = () => {
    const barcode = prompt('Enter barcode manually:');
    if (barcode && barcode.trim()) {
      onBarcodeDetected(barcode.trim(), 'manual');
    }
  };

  const handleTapToFocus = async (event, track) => {
    console.log('🔍 handleTapToFocus called!');
    setFocusStatus('Attempting to focus...');
    
    const video = videoRef.current;
    if (!video) {
      console.log('❌ No video element');
      setFocusStatus('Error: No video element');
      return;
    }
    
    try {
      // Method 1: Try ImageCapture API for better mobile focus control
      if (track && window.ImageCapture) {
        console.log('📷 Trying ImageCapture API...');
        setFocusStatus('Trying ImageCapture API...');
        
        try {
          const imageCapture = new ImageCapture(track);
          const capabilities = await imageCapture.getPhotoCapabilities();
          
          if (capabilities.focusMode && capabilities.focusMode.includes('single-shot')) {
            await imageCapture.setOptions({
              focusMode: 'single-shot'
            });
            console.log('✅ ImageCapture focus triggered');
            setFocusStatus('✅ Focus triggered via ImageCapture');
            
            // Switch back to continuous
            setTimeout(async () => {
              try {
                await imageCapture.setOptions({ focusMode: 'continuous' });
                console.log('✅ Switched back to continuous via ImageCapture');
                setFocusStatus('Focus active');
              } catch (err) {
                console.log('⚠️ Could not switch back via ImageCapture');
              }
            }, 1500);
            return;
          }
        } catch (err) {
          console.log('⚠️ ImageCapture failed:', err.message);
          setFocusStatus('ImageCapture not supported');
        }
      }
      
      // Method 2: Try MediaStreamTrack constraints with different approaches
      if (track && track.getCapabilities) {
        const capabilities = track.getCapabilities();
        console.log('🎯 Available capabilities:', Object.keys(capabilities));
        
        const availableFeatures = [];
        if (capabilities.focusMode) availableFeatures.push('focusMode');
        if (capabilities.focusDistance) availableFeatures.push('focusDistance');
        if (capabilities.torch) availableFeatures.push('torch');
        
        setFocusStatus(`Camera features: ${availableFeatures.join(', ') || 'none'}`);
        
        // Try different focus approaches
        const focusMethods = [
          // Method 2a: Basic focus mode
          async () => {
            if (capabilities.focusMode) {
              await track.applyConstraints({ focusMode: 'single-shot' });
              return 'single-shot focus';
            }
            throw new Error('No focusMode');
          },
          
          // Method 2b: Advanced constraints
          async () => {
            if (capabilities.focusMode) {
              await track.applyConstraints({ 
                advanced: [{ focusMode: 'single-shot' }] 
              });
              return 'advanced single-shot focus';
            }
            throw new Error('No advanced focusMode');
          },
          
          // Method 2c: Focus distance
          async () => {
            if (capabilities.focusDistance) {
              await track.applyConstraints({ focusDistance: 0.1 });
              return 'close focus distance';
            }
            throw new Error('No focusDistance');
          },
          
          // Method 2d: Manual focus with distance
          async () => {
            if (capabilities.focusMode && capabilities.focusDistance) {
              await track.applyConstraints({
                advanced: [{
                  focusMode: 'manual',
                  focusDistance: 0.15
                }]
              });
              return 'manual focus with distance';
            }
            throw new Error('No manual focus');
          }
        ];
        
        for (const method of focusMethods) {
          try {
            const result = await method();
            console.log(`✅ Applied ${result}`);
            setFocusStatus(`✅ Applied ${result}`);
            
            // Switch back to continuous after success
            setTimeout(async () => {
              try {
                await track.applyConstraints({ focusMode: 'continuous' });
                console.log('✅ Switched back to continuous focus');
                setFocusStatus('Focus active');
              } catch (err) {
                console.log('⚠️ Could not switch back to continuous');
              }
            }, 2000);
            return; // Success, exit
          } catch (err) {
            console.log(`⚠️ ${err.message}`);
            continue; // Try next method
          }
        }
      }
      
      // Method 3: Force camera re-initialization (more aggressive)
      console.log('🔄 Trying camera re-initialization...');
      setFocusStatus('Trying camera refresh...');
      
      // Get current constraints
      const currentConstraints = track.getConstraints();
      
      // Apply slightly different constraints to force refresh
      await track.applyConstraints({
        ...currentConstraints,
        width: { ideal: 1280 },
        height: { ideal: 720 }
      });
      
      console.log('✅ Camera constraints refreshed');
      setFocusStatus('✅ Camera refreshed');
      
      // Clear status after a few seconds
      setTimeout(() => {
        setFocusStatus('');
      }, 3000);
      
    } catch (err) {
      console.log('❌ All focus methods failed:', err.message);
      console.log('📱 Mobile camera focus may not be supported by this browser');
      setFocusStatus('❌ Focus not supported on this device');
      
      // Clear error status after a few seconds
      setTimeout(() => {
        setFocusStatus('');
      }, 4000);
    }
  };

  const toggleTorch = async () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track && track.getCapabilities) {
        const capabilities = track.getCapabilities();
        if (capabilities.torch) {
          const currentSettings = track.getSettings();
          try {
            await track.applyConstraints({
              advanced: [{ torch: !currentSettings.torch }]
            });
          } catch (err) {
            // Try basic torch constraint
            await track.applyConstraints({
              torch: !currentSettings.torch
            });
          }
        }
      }
    }
  };


  if (error) {
    return (
      <div className="barcode-scanner">
        <div className="scanner-header">
          <h3>Barcode Scanner</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="scanner-error">
          <div className="error-icon">📷</div>
          <h4>Camera Access Required</h4>
          <p>{error}</p>
          <div className="error-actions">
            <button className="btn btn-primary" onClick={handleRetry}>
              Try Again
            </button>
            <button className="btn btn-secondary" onClick={handleManualEntry}>
              Enter Manually
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="barcode-scanner">
      <div className="scanner-header">
        <h3>Scan Barcode</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>
      
      <div className="scanner-content">
        <div className="camera-container">
          <video
            ref={videoRef}
            className="camera-video"
            playsInline
            muted
          />
          <canvas
            ref={canvasRef}
            className="scanner-canvas"
            style={{ display: 'none' }}
          />
          
          {/* Scanning overlay */}
          <div className="scanning-overlay">
            <div className="scan-frame">
              <div className="scan-corners">
                <div className="corner top-left"></div>
                <div className="corner top-right"></div>
                <div className="corner bottom-left"></div>
                <div className="corner bottom-right"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="scanner-instructions">
          <p>📱 Point your camera at the barcode on the whisky bottle</p>
          <p>🎯 Align the barcode within the frame</p>
          {isScanning && (
            <>
              <div className="scanning-indicator">
                <div className="pulse"></div>
                <span>Scanning for barcode...</span>
              </div>
              <div className="debug-info">
                {focusStatus && (
                  <>
                    <br />
                    <small style={{color: '#e74c3c', fontWeight: 'bold'}}>🎯 {focusStatus}</small>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="scanner-actions">
          <button className="btn btn-secondary" onClick={handleManualEntry}>
            Enter Manually
          </button>
          <button className="btn btn-primary" onClick={switchToNextCamera}>
            🔄 Switch Camera {availableCameras.length > 1 ? `(${currentCameraIndex + 1}/${availableCameras.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
